import { BeaconClient, buildGeo, buildMediaRefs, type BlobUploader, type ProcessedImage } from "@beacon/client-core";

/** UI-level report intent. `note` is plaintext here; it is sealed before publish.
 *  Corroboration provenance is NOT a wire field (a corroboration is just another
 *  report of the same hazard in the cluster) so it never appears in this type. */
export interface ReportInput {
  hazard: string; // controlled vocab: fire|flood|earthquake|conflict|other (event token)
  severity: number; // 1..3
  cell: string; // res-8 H3 cell ("here" GPS fix or a picked "over there")
  note?: string;
  media?: ProcessedImage[]; // browser-processed photos (downscaled/thumbed/blurhashed); E1
}

/** Map legacy hazard event token to CAP category (F0a). */
const HAZARD_CATEGORY: Record<string, string> = {
  fire: "Fire", flood: "Met", earthquake: "Geo", conflict: "Security", other: "Other",
};

const utf8 = (s: string) => new TextEncoder().encode(s);

/** "I see it too": the corroborating report for an incident. It is the incident's hazard AT
 *  THE INCIDENT'S CELL — never the confirmer's home or live cell — so (a) the confirmer's
 *  position is not put on the wire and (b) corroboration folds it into the same hazard+cell
 *  cluster instead of opening a second incident one neighbourhood over. Null when the
 *  incident carries no cell: there is nothing to corroborate against and no fallback. */
export function corroborationInput(view: { hazard: string; cell: string }): ReportInput | null {
  if (!view.cell) return null;
  return { hazard: view.hazard, severity: 2, cell: view.cell, note: "" };
}

/**
 * Publish a real signed app.aid.report through the client's transport.
 *
 * The optional note AND any photos seal under ONE epoch snapshot (identical
 * keyRef) so shredding the report kills the photos (photo spec §1, no-roadblock
 * #3 — only ciphertext hits the wire). buildEnvelope validates against the
 * catalog, so an invalid report (e.g. severity out of range) throws before
 * anything is sent.
 *
 * Photos are best-effort: a failed photo NEVER fails the report (photo spec §6).
 */
export async function publishReport(client: BeaconClient, input: ReportInput, uploader?: BlobUploader) {
  const payload: Record<string, unknown> = {
    classification: { category: HAZARD_CATEGORY[input.hazard] ?? "Other", event: input.hazard },
    severity: input.severity,
  };
  const snap = await client.epochSnapshot();
  const note = input.note?.trim();
  if (note) payload.note = await client.sealBroadcastWith(snap, utf8(note));
  if (input.media?.length && uploader) {
    try {
      const refs = await buildMediaRefs(client, snap, input.media, uploader);
      if (refs.length) payload.media = refs;
    } catch {
      // drop photos; the report still sends (photo spec §6)
    }
  }
  return client.publish({ $type: "app.aid.report", payload, geo: buildGeo(input.cell) });
}
